// Valutatore offline della checklist dell'audit (specchio del motore Python `safety/engine.py`).
// Calcola SOLO quali domande sono attive: la valutazione del rischio resta sul server, che ricalcola tutto all'invio.
// Nessuna dipendenza: gira sia nel browser sia sotto Node (il test di equivalenza lo confronta col motore Python).

const SURVEY = "_";
const AnswerTypes = { EQUIPMENT_MULTI: "equipment_multi" };

export function akey(scope, qcode, equipment) {
  return `${scope}|${qcode}${equipment ? `@${equipment}` : ""}`;
}

function valueForCtx(a) {
  if (a.state === "yes") return "yes";
  if (a.state === "no") return "no";
  if (a.state === "answered") return a.value === undefined ? null : a.value;
  return null;
}

const num = (x) => {
  if (x === null || x === undefined || x === "" || typeof x === "boolean") return null;
  const n = Number(x);
  return Number.isFinite(n) ? n : null;
};

const eq = (a, b) => JSON.stringify(a) === JSON.stringify(b);

export function checkCond(c, ctx) {
  const actual = ctx[c.field] === undefined ? null : ctx[c.field];
  const expected = c.value;
  const empty = actual === null || actual === "" || (Array.isArray(actual) && actual.length === 0);
  if (c.operator === "EXISTS") return !empty;
  if (c.operator === "NOT_EXISTS") return empty;
  if (actual === null) return false;
  switch (c.operator) {
    case "EQ": return eq(actual, expected);
    case "NE": return !eq(actual, expected);
    case "GT": case "GTE": case "LT": case "LTE": {
      const a = num(actual), b = num(expected);
      if (a === null || b === null) return false;
      return { GT: a > b, GTE: a >= b, LT: a < b, LTE: a <= b }[c.operator];
    }
    case "IN":
      return Array.isArray(actual) ? actual.some((x) => expected.includes(x)) : expected.includes(actual);
    case "NOT_IN":
      return Array.isArray(actual) ? !actual.some((x) => expected.includes(x)) : !expected.includes(actual);
    case "CONTAINS":
      if (Array.isArray(actual)) return actual.some((x) => eq(x, expected));
      return typeof actual === "string" && actual.includes(String(expected));
    default: return false;
  }
}

function ruleFires(rule, ctx) {
  if (!rule.conds.length) return true;
  return rule.logic === "AND" ? rule.conds.every((c) => checkCond(c, ctx)) : rule.conds.some((c) => checkCond(c, ctx));
}

export function evaluateChecklist(bundle, answers) {
  const Q = bundle.questions;
  const activeKeys = [];
  const meta = {};
  const scopeOf = (task, qcode) => (Q[qcode] && Q[qcode].scope === "survey" ? SURVEY : task);
  const addKey = (task, qcode, equipment) => {
    const scope = scopeOf(task, qcode);
    const k = akey(scope, qcode, equipment);
    if (!meta[k]) {
      meta[k] = { task: scope === SURVEY ? null : task, question: qcode, equipment: equipment || null };
      activeKeys.push(k);
    }
    return k;
  };
  const sigOf = (k) => {
    const q = Q[meta[k].question];
    if (q.own) return `own:${k}`;
    return `${q.sig}|${meta[k].equipment || ""}`;
  };
  const effective = () => {
    const eff = { ...answers };
    for (const t of bundle.task_order) {
      const ex = bundle.tasks[t].exec;
      if (ex && Q[ex]) {
        const k0 = akey(scopeOf(t, ex), ex);
        if (!eff[k0]) eff[k0] = { question_code: ex, state: "yes", value: null, virtual: "selected" };
      }
    }
    const firstReal = {};
    for (const k of activeKeys) if (answers[k]) { const s = sigOf(k); if (!(s in firstReal)) firstReal[s] = k; }
    for (const k of activeKeys) {
      if (!eff[k]) {
        const src = firstReal[sigOf(k)];
        if (src) eff[k] = { ...answers[src], virtual: "twin" };
      }
    }
    return eff;
  };
  const ctxFor = (task, eff) => {
    const ctx = {};
    for (const k of activeKeys) {
      const scope = k.split("|", 1)[0];
      if (scope !== SURVEY && scope !== task) continue;
      const a = eff[k];
      if (!a || meta[k].equipment) continue;
      ctx[`answer.${meta[k].question}`] = valueForCtx(a);
      ctx[`state.${meta[k].question}`] = a.state;
    }
    return ctx;
  };

  for (const t of bundle.task_order) {
    const task = bundle.tasks[t];
    for (const q of task.entry) addKey(t, q);
    for (let i = 0; i < 40; i++) {
      const before = activeKeys.length;
      const ctx = ctxFor(t, effective());
      for (const rc of task.rules) {
        const rule = bundle.rules[rc];
        if (ruleFires(rule, ctx)) for (const target of rule.ask) addKey(t, target);
      }
      if (activeKeys.length === before) break;
    }
    const eff = effective();
    for (const k of [...activeKeys]) {
      const m = meta[k];
      const q = Q[m.question];
      if (!q || q.type !== AnswerTypes.EQUIPMENT_MULTI || m.equipment) continue;
      if (m.task !== t && m.task !== null) continue;
      const a = eff[k];
      if (!a || a.state !== "answered") continue;
      for (const code of a.value || []) {
        const e = bundle.equipment[code];
        if (e) for (const dq of e.detail) addKey(t, dq, code);
      }
    }
  }

  const eff = effective();
  const missing = [];
  const visible = [];
  const seen = new Set();
  for (const k of activeKeys) {
    const q = Q[meta[k].question];
    const a = eff[k];
    if (a && a.virtual === "selected") continue;
    const s = sigOf(k);
    if (!s.startsWith("own:")) {
      if (seen.has(s)) continue;
      seen.add(s);
    }
    visible.push(k);
    if (!a && q.required) missing.push(k);
  }
  return { activeKeys, visible, missing, meta };
}
